import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../auth/context/AuthContext';
import { FaList, FaSpinner, FaPlus, FaSearch, FaCheck } from 'react-icons/fa';
import { motion } from 'framer-motion';
import api from '../../services/api';
const CustomerPlaylists = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { customerToken } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [isCreatingList, setIsCreatingList] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    const fetchPlaylists = async () => {
      try {
        const response = await api.get('/customers/lists', {
          headers: { Authorization: `Bearer ${customerToken}` },
        });
        setPlaylists(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch playlists. Please try again.');
        setLoading(false);
      }
    };

    fetchPlaylists();
  }, [customerToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        setShowCreateDialog(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredPlaylists = playlists.filter((playlist) =>
    playlist.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getGradientColor = (index) => {
    const colors = [
      'from-blue-400 to-indigo-500',
      'from-green-400 to-teal-500',
      'from-yellow-400 to-orange-500',
      'from-pink-400 to-purple-500',
      'from-red-400 to-rose-500',
    ];
    return colors[index % colors.length];
  };

  const handleCreateNewList = async () => {
    if (!newListName.trim()) return;

    setIsCreatingList(true);
    try {
      const response = await api.post(
        '/customers/lists',
        { name: newListName, type: 'regular' },
        { headers: { Authorization: `Bearer ${customerToken}` } }
      );
      setPlaylists([...playlists, response.data]);
      setNewListName('');
      setShowCreateDialog(false);
    } catch (error) {
      setError('Failed to create new list. Please try again.');
    } finally {
      setIsCreatingList(false);
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <FaSpinner className='animate-spin text-4xl text-blue-500' />
      </div>
    );
  }

  if (error) {
    return <div className='text-red-500 text-center'>{error}</div>;
  }

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='flex justify-between items-center mb-6'>
        <h1 className='text-3xl font-bold'>Your Playlists</h1>
        <button
          onClick={() => setShowCreateDialog(true)}
          className='bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-full flex items-center transition duration-300'
        >
          <FaPlus className='mr-2' /> Create Playlist
        </button>
      </div>

      <div className='mb-6'>
        <div className='relative'>
          <input
            type='text'
            placeholder='Search playlists...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full pl-10 pr-4 py-3 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          <FaSearch className='absolute left-3 top-3 text-gray-400' />
        </div>
      </div>

      {filteredPlaylists.length === 0 ? (
        <div className='text-center py-10'>
          <FaList className='text-6xl text-gray-300 mx-auto mb-4' />
          <p className='text-xl text-gray-500'>No playlists found.</p>
          <p className='text-gray-400 mt-2'>
            Create a new playlist to get started!
          </p>
        </div>
      ) : (
        <motion.div
          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {filteredPlaylists.map((playlist, index) => (
            <motion.div
              key={playlist._id}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to={`/playlists/${playlist._id}`}
                className={`bg-gradient-to-br ${getGradientColor(
                  index
                )} text-white shadow-md rounded-lg p-6 hover:shadow-lg transition-all duration-300 block h-full`}
              >
                <div className='flex items-center mb-4'>
                  {/* <FaList className='text-3xl mr-3' /> */}
                  <h2 className='text-2xl font-semibold'>{playlist.name}</h2>
                </div>
                {/* {playlist.restaurants.length === 0 ? (
                  <div className='text-center py-4'>
                    <FaList className='text-4xl mx-auto mb-2 opacity-50' />
                    <p className='text-sm'>This playlist is empty.</p>
                  </div>
                ) : ( */}
                <p className='text-lg mb-2'>
                  {playlist.restaurants.length} store
                  {playlist.restaurants.length !== 1 ? 's' : ''}
                </p>
                {/* )} */}
                <p className='text-sm opacity-75'>
                  Created on {new Date(playlist.createdAt).toLocaleDateString()}
                </p>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}

      {showCreateDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div ref={dialogRef} className='bg-white p-6 rounded-lg w-96'>
            <h2 className='text-xl font-bold mb-4'>Create New Playlist</h2>
            <div className='mb-4'>
              <input
                type='text'
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder='Enter playlist name'
                className='w-full p-2 border rounded'
              />
            </div>
            <div className='flex justify-end'>
              <button
                onClick={() => setShowCreateDialog(false)}
                className='mr-2 px-4 py-2 text-gray-600 hover:text-gray-800'
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewList}
                disabled={isCreatingList}
                className='bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded flex items-center'
              >
                {isCreatingList ? (
                  <>
                    <FaSpinner className='animate-spin mr-2' />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPlaylists;
// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import axios from 'axios';
// import { useAuth } from '../auth/context/AuthContext';
// import {
//   FaList,
//   FaSpinner,
//   FaPlus,
//   FaSearch,
//   FaUtensils,
// } from 'react-icons/fa';
// import { motion } from 'framer-motion';

// const CustomerPlaylists = () => {
//   const [playlists, setPlaylists] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [searchTerm, setSearchTerm] = useState('');
//   const { customerToken } = useAuth();

//   useEffect(() => {
//     const fetchPlaylists = async () => {
//       try {
//         const response = await axios.get('/api/customers/lists', {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         });
//         setPlaylists(response.data);
//         setLoading(false);
//       } catch (err) {
//         setError('Failed to fetch playlists. Please try again.');
//         setLoading(false);
//       }
//     };

//     fetchPlaylists();
//   }, [customerToken]);

//   const filteredPlaylists = playlists.filter((playlist) =>
//     playlist.name.toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   const getGradientColor = (index) => {
//     const colors = [
//       'from-blue-400 to-indigo-500',
//       'from-green-400 to-teal-500',
//       'from-yellow-400 to-orange-500',
//       'from-pink-400 to-purple-500',
//       'from-red-400 to-rose-500',
//     ];
//     return colors[index % colors.length];
//   };

//   if (loading) {
//     return (
//       <div className='flex justify-center items-center h-screen'>
//         <FaSpinner className='animate-spin text-4xl text-blue-500' />
//       </div>
//     );
//   }

//   if (error) {
//     return <div className='text-red-500 text-center'>{error}</div>;
//   }

//   return (
//     <div className='container mx-auto px-4 py-8'>
//       <div className='flex justify-between items-center mb-6'>
//         <h1 className='text-3xl font-bold'>Your Playlists</h1>
//         <Link
//           to='/create-playlist'
//           className='bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-full flex items-center transition duration-300'
//         >
//           <FaPlus className='mr-2' /> Create Playlist
//         </Link>
//       </div>

//       <div className='mb-6'>
//         <div className='relative'>
//           <input
//             type='text'
//             placeholder='Search playlists...'
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             className='w-full pl-10 pr-4 py-3 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500'
//           />
//           <FaSearch className='absolute left-3 top-3 text-gray-400' />
//         </div>
//       </div>

//       {filteredPlaylists.length === 0 ? (
//         <div className='text-center py-10'>
//           <FaList className='text-6xl text-gray-300 mx-auto mb-4' />
//           <p className='text-xl text-gray-500'>No playlists found.</p>
//           <p className='text-gray-400 mt-2'>
//             Create a new playlist to get started!
//           </p>
//         </div>
//       ) : (
//         <motion.div
//           className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           transition={{ duration: 0.5 }}
//         >
//           {filteredPlaylists.map((playlist, index) => (
//             <motion.div
//               key={playlist._id}
//               whileHover={{ scale: 1.03 }}
//               whileTap={{ scale: 0.98 }}
//             >
//               <Link
//                 to={`/playlists/${playlist._id}`}
//                 className={`bg-gradient-to-br ${getGradientColor(
//                   index
//                 )} text-white shadow-md rounded-lg p-6 hover:shadow-lg transition-all duration-300 block h-full`}
//               >
//                 <div className='flex items-center mb-4'>
//                   <FaList className='text-3xl mr-3' />
//                   <h2 className='text-2xl font-semibold'>{playlist.name}</h2>
//                 </div>
//                 {/* {playlist.restaurants.length === 0 ? (
//                   <div className='text-center py-4'>
//                     <FaUtensils className='text-4xl mx-auto mb-2 opacity-50' />
//                     <p className='text-sm'>This playlist is empty.</p>
//                   </div>
//                 ) : ( */}
//                 <p className='text-lg mb-2'>
//                   {playlist.restaurants.length} shop
//                   {playlist.restaurants.length !== 1 ? 's' : ''}
//                 </p>
//                 {/* )} */}
//                 <p className='text-sm opacity-75'>
//                   Created on {new Date(playlist.createdAt).toLocaleDateString()}
//                 </p>
//               </Link>
//             </motion.div>
//           ))}
//         </motion.div>
//       )}
//     </div>
//   );
// };

// export default CustomerPlaylists;
